import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  X,
  MessageSquare,
  Clock,
  User,
  Building2,
  Send,
  Bot,
  Loader,
  RefreshCw,
  ShieldCheck,
  ZoomIn,
  FileImage,
  Check,
  AlertCircle,
  Minus,
  Phone,
  Loader2,
  Wallet
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useApp, PAKISTAN_BANKS } from '../../context/AppContext';
import { useBrokerData } from '../../context/BrokerDataContext';
import { getBrokerFromPath } from '../../config/brokerConfig';
import { whatsappAPI } from '../../services/api';
import { adminDecisionNotificationService } from '../../services/emailService';
import { verifyReceipt, getCheckDisplay } from '../../services/receiptVerificationService';
import './TransactionReview.css';

function TransactionReview() {
  const location = useLocation();
  const brokerId = getBrokerFromPath(location.pathname);

  // Get broker-specific transactions from BrokerDataContext
  const {
    transactions,
    updateTransaction: brokerUpdateTransaction,
    isLoading
  } = useBrokerData();

  // Get global functions from AppContext
  const { getActiveAIConfig, brokerUsers } = useApp();

  // Wrapper function for updateTransaction that includes brokerId
  const updateTransaction = async (transaction) => {
    return brokerUpdateTransaction(transaction.id, transaction);
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageTransaction, setMessageTransaction] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [messageType, setMessageType] = useState('payment_received');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [showRawResponse, setShowRawResponse] = useState(false);

  // Approval/Rejection Modal States
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [pendingTransaction, setPendingTransaction] = useState(null);

  // AI Verification States
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationError, setVerificationError] = useState(null);
  const retryTimeoutRef = useRef(null);

  // Get the active AI config from context
  const activeAIConfig = getActiveAIConfig();

  // Load existing AI verification result when transaction is selected
  useEffect(() => {
    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (selectedTransaction) {
      // If transaction already has AI verification result, use it
      if (selectedTransaction.aiVerificationResult) {
        console.log('[AI Verification] Using existing verification result');
        setVerificationResult(selectedTransaction.aiVerificationResult);
        setVerificationError(null);
      } else if (
        // Only run if transaction has receipt, AI is configured, and not already verifying
        (selectedTransaction.receiptBase64 || selectedTransaction.receiptUrl) &&
        activeAIConfig &&
        !verificationResult &&
        !isVerifying
      ) {
        // No existing result, run AI verification
        console.log('[AI Verification] No existing result, running verification...');
        const timer = setTimeout(() => {
          handleAIVerification(selectedTransaction);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [selectedTransaction, activeAIConfig]);

  // Auto-retry on error after 1 minute
  useEffect(() => {
    if (verificationError && selectedTransaction && !isVerifying) {
      console.log('[AI Verification] Error detected, will retry in 1 minute...');
      retryTimeoutRef.current = setTimeout(() => {
        console.log('[AI Verification] Retrying...');
        setVerificationError(null);
        handleAIVerification(selectedTransaction);
      }, 60000); // 1 minute

      return () => {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
      };
    }
  }, [verificationError, selectedTransaction, isVerifying]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Get bank logo
  const getBankLogo = (bankCode) => {
    const bank = PAKISTAN_BANKS.find(b => b.code === bankCode);
    const isWallet = bank?.type === 'wallet';
    const logoUrl = bank?.logo;

    return (
      <div className="bank-logo-wrapper">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={bank?.name || bankCode}
            className="bank-logo"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="bank-logo-fallback" style={{ display: logoUrl ? 'none' : 'flex' }}>
          {isWallet ? <Wallet size={18} /> : <Building2 size={18} />}
        </div>
      </div>
    );
  };

  // Filter transactions
  const filteredTransactions = transactions
    .filter(t => {
      const matchesSearch =
        t.referenceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.userCnic?.includes(searchTerm);

      // Filter logic:
      // Pending = All transactions that need approval (not completed, not rejected) - both matched & mismatched
      // Matched = Only matched transactions (not completed, not rejected)
      // Manual = Only mismatched transactions (not completed, not rejected)
      // Completed = Approved transactions (both matched & mismatched)
      // Rejected = Rejected transactions (both matched & mismatched)
      const needsReview = t.status !== 'completed' && t.status !== 'rejected';
      const isMatched = t.verificationStatus === 'matched';
      const isMismatched = t.verificationStatus === 'mismatched' || t.verificationStatus !== 'matched';

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'pending' && needsReview) ||
        (statusFilter === 'matched' && isMatched && needsReview) ||
        (statusFilter === 'manual_review' && !isMatched && needsReview) ||
        (statusFilter === 'completed' && t.status === 'completed') ||
        (statusFilter === 'rejected' && t.status === 'rejected');

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // AI Verification Handler
  const handleAIVerification = async (transaction) => {
    if (!activeAIConfig) {
      setVerificationError('No AI configuration found. Please configure AI in Settings.');
      return;
    }

    const receiptImage = transaction.receiptBase64 || transaction.receiptUrl;
    if (!receiptImage) {
      setVerificationError('No receipt image available for verification.');
      return;
    }

    setIsVerifying(true);
    setVerificationError(null);
    setVerificationResult(null);

    try {
      // NOTE: CNIC validation is done separately from user data, NOT sent to AI
      const caseData = {
        expectedAmount: transaction.amount,
        senderAccount: transaction.bankAccountNumber,
        expectedAccountTitle: transaction.bankAccountTitle || transaction.userName,
        expectedBank: transaction.bankName
        // CNIC is validated locally from user data, not by AI
      };

      console.log('[AI Verification] Starting verification with case data:', caseData);

      const result = await verifyReceipt(activeAIConfig, receiptImage, caseData);

      console.log('[AI Verification] Result:', result);

      if (result.success) {
        setVerificationResult(result.result);

        // Save AI verification result to transaction
        updateTransaction({
          id: transaction.id,
          aiVerificationStatus: result.result.overall_status,
          aiVerificationResult: result.result,
          aiVerifiedAt: new Date().toISOString()
        });

        // Update selected transaction
        setSelectedTransaction(prev => ({
          ...prev,
          aiVerificationStatus: result.result.overall_status,
          aiVerificationResult: result.result,
          aiVerifiedAt: new Date().toISOString()
        }));
      } else {
        setVerificationError(result.error || 'Verification failed');
      }
    } catch (error) {
      console.error('[AI Verification] Error:', error);
      setVerificationError(error.message || 'An error occurred during verification');
    } finally {
      setIsVerifying(false);
    }
  };

  // Open approval modal
  const handleApproveClick = (transaction) => {
    setPendingTransaction(transaction);
    setApproveComment('');
    setShowApproveModal(true);
  };

  // Confirm approval with comment
  const handleApproveConfirm = async () => {
    if (!pendingTransaction) return;

    setIsProcessing(true);
    const approvedAt = new Date().toISOString();

    try {
      // Update transaction in state/database (only await this - it's essential)
      await updateTransaction({
        id: pendingTransaction.id,
        status: 'completed',
        approvedAt: approvedAt,
        adminComment: approveComment,
      });

      // Send all notifications in background (non-blocking for speed)
      if (pendingTransaction.userPhone) {
        whatsappAPI.sendPaymentConfirmation(
          pendingTransaction.userPhone,
          pendingTransaction.id,
          pendingTransaction.amount || 0,
          'success'
        ).catch(err => console.log('WhatsApp notification failed:', err));
      }

      if (pendingTransaction.userEmail || pendingTransaction.email) {
        adminDecisionNotificationService.sendTransactionDecisionNotification({
          email: pendingTransaction.userEmail || pendingTransaction.email,
          userName: pendingTransaction.userName,
          transactionId: pendingTransaction.id,
          referenceNumber: pendingTransaction.referenceNumber,
          amount: pendingTransaction.amount,
          bankName: pendingTransaction.bankName,
          accountTitle: pendingTransaction.bankAccountTitle,
          decision: 'approved',
          adminComment: approveComment,
          documentBase64: pendingTransaction.receiptBase64,
          documentName: pendingTransaction.receiptName || `receipt-${pendingTransaction.referenceNumber}.jpg`,
          documentType: pendingTransaction.receiptType || 'image/jpeg',
        }).catch(err => console.error('[Admin] Failed to send approval email:', err));
      }

      // Send topup notification in background
      fetch('http://localhost:5000/api/send-topup-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: pendingTransaction.userName,
          userCnic: pendingTransaction.userCnic,
          userEmail: pendingTransaction.userEmail || pendingTransaction.email,
          userPhone: pendingTransaction.userPhone,
          referenceNumber: pendingTransaction.referenceNumber,
          amount: pendingTransaction.amount,
          bankName: pendingTransaction.bankName,
          accountTitle: pendingTransaction.bankAccountTitle,
          transactionId: pendingTransaction.id,
          brokerName: pendingTransaction.brokerName,
          approvedAt: approvedAt,
          adminComment: approveComment,
        }),
      }).catch(err => console.error('[Admin] Failed to send topup notification:', err));

      // Close modal immediately
      setShowApproveModal(false);
      setPendingTransaction(null);
      setApproveComment('');
      setSelectedTransaction(null);
      setVerificationResult(null);
    } catch (error) {
      console.error('[Admin] Approval error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Open rejection modal
  const handleRejectClick = (transaction) => {
    setPendingTransaction(transaction);
    setRejectReason('');
    setShowRejectModal(true);
  };

  // Confirm rejection with reason
  const handleRejectConfirm = async () => {
    if (!pendingTransaction || !rejectReason.trim()) return;

    setIsProcessing(true);

    try {
      // Update transaction in state/database
      await updateTransaction({
        id: pendingTransaction.id,
        status: 'rejected',
        rejectedAt: new Date().toISOString(),
        rejectionReason: rejectReason,
      });

      // Send notifications in background (non-blocking)
      if (pendingTransaction.userPhone) {
        whatsappAPI.sendPaymentConfirmation(
          pendingTransaction.userPhone,
          pendingTransaction.id,
          pendingTransaction.amount || 0,
          'failed'
        ).catch(err => console.log('WhatsApp notification failed:', err));
      }

      if (pendingTransaction.userEmail || pendingTransaction.email) {
        adminDecisionNotificationService.sendTransactionDecisionNotification({
          email: pendingTransaction.userEmail || pendingTransaction.email,
          userName: pendingTransaction.userName,
          transactionId: pendingTransaction.id,
          referenceNumber: pendingTransaction.referenceNumber,
          amount: pendingTransaction.amount,
          bankName: pendingTransaction.bankName,
          accountTitle: pendingTransaction.bankAccountTitle,
          decision: 'rejected',
          rejectionReason: rejectReason,
          documentBase64: pendingTransaction.receiptBase64,
          documentName: pendingTransaction.receiptName || `receipt-${pendingTransaction.referenceNumber}.jpg`,
          documentType: pendingTransaction.receiptType || 'image/jpeg',
        }).catch(err => console.error('[Admin] Failed to send rejection email:', err));
      }

      // Close modal and reset state immediately
      setShowRejectModal(false);
      setPendingTransaction(null);
      setRejectReason('');
      setSelectedTransaction(null);
      setVerificationResult(null);
    } catch (error) {
      console.error('[Admin] Rejection error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const openMessageModal = (transaction) => {
    const phone = '971561110052';
    let message = '';

    if (transaction.status === 'completed') {
      message = `*Team ANB PAY*\n\nDear ${transaction.userName || 'Valued Customer'},\n\nYour transaction has been completed successfully!\n\n*Transaction Details:*\n- Reference No: ${transaction.referenceNumber}\n- Amount: PKR ${(transaction.amount || 0).toLocaleString()}\n- Status: Approved\n\nYour account has been credited. Thank you for choosing ANB PAY!\n\nBest Regards,\nTeam ANB PAY`;
    } else if (transaction.verificationStatus === 'mismatched') {
      const deductionAmount = Math.round((transaction.amount || 0) * 0.05);
      const refundAmount = (transaction.amount || 0) - deductionAmount;
      message = `*Team ANB PAY*\n\nDear ${transaction.userName || 'Valued Customer'},\n\nWe regret to inform you that your transaction could not be processed.\n\n*Transaction Details:*\n- Reference No: ${transaction.referenceNumber}\n- Amount: PKR ${(transaction.amount || 0).toLocaleString()}\n- Status: Mismatched\n\n*Reason:* The payment was made from a different account than the one registered.\n\n*Refund Details:*\n- Processing Fee (5%): PKR ${deductionAmount.toLocaleString()}\n- Refund Amount: PKR ${refundAmount.toLocaleString()}\n\nYour refund will be credited within 15 minutes.\n\nBest Regards,\nTeam ANB PAY`;
    } else {
      message = `*Team ANB PAY*\n\n*Transaction Details:*\n- Reference No: ${transaction.referenceNumber}\n- Customer Name: ${transaction.userName || 'N/A'}\n- Amount: PKR ${(transaction.amount || 0).toLocaleString()}\n- Status: ${transaction.status || 'Pending'}\n\nMessage:\n`;
    }

    const userPhone = transaction.userPhone?.replace(/[^0-9]/g, '') || '';
    const whatsappUrl = userPhone
      ? `https://wa.me/${userPhone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, '_blank');
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setMessageTransaction(null);
    setCustomMessage('');
    setMessageSent(false);
  };

  const messageTemplates = {
    payment_received: (t) => `Dear ${t.userName || 'Customer'},\n\nYour payment of PKR ${(t.amount || 0).toLocaleString()} has been received.\n\nReference: ${t.referenceNumber}\n\nThank you!`,
    payment_confirmed: (t) => `Dear ${t.userName || 'Customer'},\n\nYour payment has been verified and confirmed!\n\nAmount: PKR ${(t.amount || 0).toLocaleString()}\nReference: ${t.referenceNumber}\n\nYour account will be credited shortly.`,
    payment_issue: (t) => `Dear ${t.userName || 'Customer'},\n\nWe noticed an issue with your recent payment.\n\nReference: ${t.referenceNumber}\n\nPlease contact support.`,
    custom: () => customMessage
  };

  const handleSendMessage = async () => {
    if (!messageTransaction) return;

    setIsSendingMessage(true);
    const message = messageType === 'custom' ? customMessage : messageTemplates[messageType](messageTransaction);

    try {
      await whatsappAPI.sendMessage(messageTransaction.userPhone, message);
      updateTransaction({
        id: messageTransaction.id,
        lastMessageSent: new Date().toISOString(),
        lastMessageType: messageType,
      });
      setMessageSent(true);
    } catch (error) {
      console.error('Failed to send message:', error);
    }

    setIsSendingMessage(false);
  };

  const getStatusBadge = (status, verificationStatus) => {
    if (verificationStatus === 'mismatched') {
      return <span className="status-badge warning">Mismatched</span>;
    }
    switch (status) {
      case 'pending_review':
        return <span className="status-badge pending">Pending Review</span>;
      case 'pending_payment':
        return <span className="status-badge info">Awaiting Payment</span>;
      case 'completed':
        return <span className="status-badge success">Completed</span>;
      case 'rejected':
        return <span className="status-badge danger">Rejected</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  const getVerificationBadge = (status) => {
    if (status === 'matched') {
      return (
        <span className="verification-badge matched">
          <CheckCircle size={14} /> Matched
        </span>
      );
    }
    return (
      <span className="verification-badge mismatched">
        <AlertTriangle size={14} /> Mismatched
      </span>
    );
  };

  // Calculate match percentage for a transaction
  const calculateMatchPercentage = (transaction) => {
    const extractedData = transaction.aiVerificationResult?.extracted ||
                          transaction.aiVerificationResult?.extracted_data ||
                          transaction.aiExtractedData || {};

    // Helper to parse amount
    const parseAmount = (val) => {
      if (!val) return null;
      if (typeof val === 'number') return val;
      const cleaned = String(val).replace(/[PKR,\s]/gi, '').trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    // Check Account Holder match
    const expectedName = transaction.userName || transaction.bankAccountTitle || '';
    const extractedName = extractedData.account_title || extractedData.accountTitle || '';
    const normalizeStr = (s) => s.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
    const expectedWords = normalizeStr(expectedName).split(/\s+/).filter(w => w.length > 1);
    const extractedWords = normalizeStr(extractedName).split(/\s+/).filter(w => w.length > 1);
    const matchedWords = expectedWords.filter(ew => extractedWords.some(xw => xw === ew || ew.includes(xw) || xw.includes(ew)));
    const minWords = Math.min(expectedWords.length, extractedWords.length);
    const accountHolderMatch = extractedName && (matchedWords.length >= 2 || (minWords > 0 && matchedWords.length / minWords >= 0.7));

    // Check Amount match
    const expectedAmount = parseAmount(transaction.amount);
    const extractedAmount = parseAmount(extractedData.amount);
    const amountMatch = extractedAmount && expectedAmount && extractedAmount === expectedAmount;

    // Check Account Number match
    const expectedAccNum = transaction.bankAccountNumber || '';
    const extractedAccNum = extractedData.sender_account || extractedData.senderAccount || extractedData.account_number || '';
    const accountNumberMatch = extractedAccNum && (expectedAccNum.includes(extractedAccNum.replace(/x/gi, '')) || extractedAccNum.includes(expectedAccNum.slice(-4)));

    // BASE 75%: All 3 critical fields must match
    const criticalFieldsMatch = accountHolderMatch && amountMatch && accountNumberMatch;
    if (!criticalFieldsMatch) {
      return 0;
    }

    let matchPercentage = 75;

    // +12.5% for Bank Transaction ID found
    const bankTxnId = extractedData.transaction_id || extractedData.transactionId ||
                      extractedData.reference_id || extractedData.referenceId || '';
    if (bankTxnId && bankTxnId.trim() !== '') {
      matchPercentage += 12.5;
    }

    // +12.5% for CNIC not expired
    const userCnic = transaction.userCnic || transaction.cnic;
    const user = brokerUsers?.find(u =>
      u.cnic === userCnic || u.cnic?.replace(/-/g, '') === userCnic?.replace(/-/g, '')
    ) || brokerUsers?.find(u => u.name?.toLowerCase() === (transaction.userName || transaction.bankAccountTitle)?.toLowerCase());

    const cnicExpiry = transaction.cnicExpiry || transaction.userCnicExpiry || user?.cnicExpiry;
    if (cnicExpiry) {
      let expiryDate;
      if (cnicExpiry.includes('-')) {
        const parts = cnicExpiry.split('-');
        if (parts[0].length === 4) {
          expiryDate = new Date(cnicExpiry);
        } else {
          expiryDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
      } else {
        expiryDate = new Date(cnicExpiry);
      }
      if (expiryDate > new Date()) {
        matchPercentage += 12.5;
      }
    }

    return matchPercentage;
  };

  // AI Match Percentage Badge for transaction cards
  const getAIMatchBadge = (transaction) => {
    if (!transaction.aiVerificationResult && !transaction.aiVerificationStatus) return null;

    const matchPercentage = calculateMatchPercentage(transaction);

    let badgeClass = 'failed';
    if (matchPercentage >= 100) badgeClass = 'approved';
    else if (matchPercentage >= 75) badgeClass = 'good';
    else if (matchPercentage > 0) badgeClass = 'partial';

    return (
      <span className={`ai-match-badge ${badgeClass}`}>
        {matchPercentage}%
      </span>
    );
  };

  // Verification check item component
  const VerificationCheckItem = ({ label, value, expected, extracted }) => {
    const display = getCheckDisplay(value);
    const IconComponent = value === true ? Check : value === false ? X : value === 'UNVERIFIABLE' || value === 'MISSING' ? AlertCircle : Minus;

    return (
      <div className={`verification-check-item ${value === true ? 'pass' : value === false ? 'fail' : 'warning'}`}>
        <div className="check-icon" style={{ backgroundColor: display.color + '20', color: display.color }}>
          <IconComponent size={16} />
        </div>
        <div className="check-content">
          <span className="check-label">{label}</span>
          <div className="check-values">
            {expected && <span className="expected">Expected: {expected}</span>}
            {extracted && <span className="extracted">Found: {extracted}</span>}
          </div>
        </div>
        <span className="check-status" style={{ color: display.color }}>{display.label}</span>
      </div>
    );
  };

  const openTransactionModal = (transaction) => {
    setSelectedTransaction(transaction);
    setVerificationResult(transaction.aiVerificationResult || null);
    setVerificationError(null);
    setShowRawResponse(false);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="transaction-review">
        <div className="loading-container">
          <Loader2 size={48} className="spinning" />
          <h3>Loading Transactions...</h3>
          <p>Fetching transaction data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-review">
      {/* Header */}
      <div className="page-header">
        <h1>Transaction Review</h1>
        <p>Review and verify payment transactions</p>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <CardContent>
          <div className="filters-row">
            <div className="search-wrapper">
              <Input
                placeholder="Search by reference, name, or CNIC..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={Search}
              />
            </div>

            <div className="filter-buttons">
              <button
                className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
                onClick={() => setStatusFilter('pending')}
              >
                <Clock size={14} />
                Pending ({transactions.filter(t => t.status !== 'completed' && t.status !== 'rejected').length})
              </button>
              <button
                className={`filter-btn ${statusFilter === 'matched' ? 'active' : ''}`}
                onClick={() => setStatusFilter('matched')}
              >
                <CheckCircle size={14} />
                Matched ({transactions.filter(t => t.verificationStatus === 'matched' && t.status !== 'completed' && t.status !== 'rejected').length})
              </button>
              <button
                className={`filter-btn ${statusFilter === 'manual_review' ? 'active' : ''}`}
                onClick={() => setStatusFilter('manual_review')}
              >
                <AlertTriangle size={14} />
                Manual ({transactions.filter(t => t.verificationStatus !== 'matched' && t.status !== 'completed' && t.status !== 'rejected').length})
              </button>
              <button
                className={`filter-btn ${statusFilter === 'completed' ? 'active' : ''}`}
                onClick={() => setStatusFilter('completed')}
              >
                <CheckCircle size={14} />
                Completed ({transactions.filter(t => t.status === 'completed').length})
              </button>
              <button
                className={`filter-btn ${statusFilter === 'rejected' ? 'active' : ''}`}
                onClick={() => setStatusFilter('rejected')}
              >
                <XCircle size={14} />
                Rejected ({transactions.filter(t => t.status === 'rejected').length})
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card className="transactions-list-card">
        <CardContent>
          <div className="transactions-list">
            {filteredTransactions.map(transaction => (
              <div
                key={transaction.id}
                className={`transaction-item ${transaction.verificationStatus === 'mismatched' ? 'mismatched' : ''} ${transaction.aiVerificationStatus ? 'has-ai-status' : ''}`}
              >
                <div className="transaction-main">
                  <div className="transaction-info">
                    <div className="transaction-ref">
                      <span className="ref-label">Reference:</span>
                      <span className="ref-value">{transaction.referenceNumber}</span>
                    </div>
                    <div className="transaction-user">
                      <User size={14} />
                      <span>{transaction.userName}</span>
                    </div>
                    <div className="transaction-broker">
                      {getBankLogo(transaction.bankCode)}
                      <span>{transaction.bankName || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="transaction-amount">
                    <span className="amount-value">
                      PKR {(transaction.amount || 0).toLocaleString()}
                    </span>
                    <span className="amount-date">
                      {new Date(transaction.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="transaction-status-col">
                    {getStatusBadge(transaction.status, transaction.verificationStatus)}
                    {getAIMatchBadge(transaction)}
                  </div>

                  <div className="transaction-actions">
                    <Button
                      variant="ghost"
                      size="small"
                      icon={Eye}
                      onClick={() => openTransactionModal(transaction)}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      icon={MessageSquare}
                      onClick={() => openMessageModal(transaction)}
                    >
                      Message
                    </Button>
                    {(['pending_review', 'pending_payment', 'manual_review', 'pending'].includes(transaction.status) || !transaction.status) && (
                      <>
                        <Button
                          variant="success"
                          size="small"
                          icon={CheckCircle}
                          onClick={() => handleApproveClick(transaction)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="danger"
                          size="small"
                          icon={XCircle}
                          onClick={() => handleRejectClick(transaction)}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {transaction.status === 'completed' && (
                      <span className="status-text completed">Approved</span>
                    )}
                    {transaction.status === 'rejected' && (
                      <span className="status-text rejected">Rejected</span>
                    )}
                  </div>
                </div>

                {transaction.verificationStatus === 'mismatched' && (
                  <div className="mismatch-warning-box">
                    <div className="mismatch-header">
                      <AlertTriangle size={18} />
                      <strong>Mismatch Detected</strong>
                    </div>
                    <div className="mismatch-details-grid">
                      {transaction.mismatchDetails?.issues?.map((issue, idx) => (
                        <div key={idx} className="mismatch-issue-row">
                          <span className="issue-type">
                            {issue.type === 'name_mismatch' ? 'Name Mismatch' :
                             issue.type === 'amount_mismatch' ? 'Amount Mismatch' :
                             'Reference Mismatch'}:
                          </span>
                          <span className="issue-desc">{issue.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {filteredTransactions.length === 0 && (
              <div className="empty-state">
                <Clock size={48} />
                <h3>No transactions found</h3>
                <p>No transactions match your current filters</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Transaction Detail Modal - New Design with Slip First */}
      {selectedTransaction && (
        <div className="modal-overlay" onClick={() => { setSelectedTransaction(null); setVerificationResult(null); }}>
          <div className="modal transaction-detail-modal-new" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header-new">
              <div className="modal-header-left">
                <h2>Transaction Details</h2>
                <span className="modal-ref">#{selectedTransaction.referenceNumber}</span>
              </div>
              <div className="modal-header-right">
                {getStatusBadge(selectedTransaction.status, selectedTransaction.verificationStatus)}
                <button className="modal-close-new" onClick={() => { setSelectedTransaction(null); setVerificationResult(null); }}>
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="modal-body-new">
              {/* Receipt Section - Full Width at Top */}
              {(selectedTransaction.receiptBase64 || selectedTransaction.receiptUrl) && (
                <div className="receipt-section-top">
                  <div className="receipt-header">
                    <span className="receipt-title">
                      <FileImage size={18} />
                      Payment Receipt
                    </span>
                    <button
                      className="view-full-btn"
                      onClick={() => setZoomedImage(selectedTransaction.receiptBase64 || selectedTransaction.receiptUrl)}
                    >
                      <ZoomIn size={14} />
                      View Full Size
                    </button>
                  </div>
                  <div className="receipt-image-wrapper">
                    <img
                      src={selectedTransaction.receiptBase64 || selectedTransaction.receiptUrl}
                      alt="Payment Receipt"
                      className="receipt-image-large"
                      onClick={() => setZoomedImage(selectedTransaction.receiptBase64 || selectedTransaction.receiptUrl)}
                    />
                  </div>

                  {/* AI Verification Actions - Right after receipt */}
                  {activeAIConfig && (
                    <div className="ai-actions-bar top-position">
                      {!verificationResult && !isVerifying && (
                        <button
                          className="ai-verify-btn"
                          onClick={() => handleAIVerification(selectedTransaction)}
                        >
                          <Bot size={18} />
                          Run AI Verification
                        </button>
                      )}

                      {isVerifying && (
                        <div className="ai-verifying">
                          <Loader size={18} className="spin" />
                          <span>AI is analyzing the receipt...</span>
                        </div>
                      )}

                      {verificationError && (
                        <div className="ai-error">
                          <AlertTriangle size={16} />
                          <span>{verificationError}</span>
                          <button onClick={() => handleAIVerification(selectedTransaction)}>
                            <RefreshCw size={14} /> Retry
                          </button>
                        </div>
                      )}

                      {verificationResult && !isVerifying && (
                        <button
                          className="ai-reverify-btn"
                          onClick={() => handleAIVerification(selectedTransaction)}
                        >
                          <RefreshCw size={16} />
                          Re-verify with AI
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Data Comparison Section */}
              <div className="comparison-section">
                {/* Issues Found - Displayed ABOVE checklist */}
                {verificationResult?.failure_reasons && verificationResult.failure_reasons.length > 0 && (
                  <div className="failure-reasons-box">
                    <div className="failure-header">
                      <AlertTriangle size={16} />
                      <span>Issues Found</span>
                    </div>
                    <ul className="failure-list">
                      {verificationResult.failure_reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Match Percentage Header */}
                {(() => {
                  const matchPercentage = calculateMatchPercentage(selectedTransaction);
                  let badgeClass = 'failed';
                  if (matchPercentage >= 100) badgeClass = 'approved';
                  else if (matchPercentage >= 75) badgeClass = 'good';
                  else if (matchPercentage > 0) badgeClass = 'partial';

                  return (
                    <div className="comparison-header">
                      <span className="comparison-title">
                        <ShieldCheck size={18} />
                        Data Verification Checklist
                      </span>
                      {(selectedTransaction.aiVerificationStatus || verificationResult) && (
                        <span className={`ai-match-badge large ${badgeClass}`}>
                          {matchPercentage}%
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Column Headers */}
                <div className="comparison-columns-header">
                  <div className="column-header expected-header">
                    <span className="column-icon expected-icon">📋</span>
                    <span>Expected Data</span>
                  </div>
                  <div className="column-header extracted-header">
                    <span className="column-icon extracted-icon">🔍</span>
                    <span>Extracted from Slip</span>
                  </div>
                </div>

                {/* Checklist Items */}
                <div className="checklist-container">
                  {/* Account Holder - CRITICAL FIELD: Missing = Red */}
                  {(() => {
                    const expected = selectedTransaction.userName || selectedTransaction.bankAccountTitle || 'N/A';
                    // Support both 'extracted' (new format) and 'extracted_data' (legacy)
                    const extractedData = verificationResult?.extracted || verificationResult?.extracted_data || selectedTransaction.aiExtractedData || {};
                    const extracted = extractedData.account_title || extractedData.accountTitle || '';
                    const isMissing = !extracted;
                    // Better name matching - normalize and compare all words
                    const normalizeStr = (s) => s.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
                    const expectedWords = normalizeStr(expected).split(/\s+/).filter(w => w.length > 1);
                    const extractedWords = normalizeStr(extracted).split(/\s+/).filter(w => w.length > 1);
                    // Count how many words match
                    const matchedWords = expectedWords.filter(ew => extractedWords.some(xw => xw === ew || ew.includes(xw) || xw.includes(ew)));
                    // Require at least 2 matching words OR 70% of the shorter name to match
                    const minWords = Math.min(expectedWords.length, extractedWords.length);
                    const isMatch = !isMissing && (matchedWords.length >= 2 || (minWords > 0 && matchedWords.length / minWords >= 0.7));
                    // CRITICAL: Missing data = mismatch (red), not pending
                    return (
                      <div className={`checklist-item ${isMatch ? 'match' : 'mismatch'}`}>
                        <div className="checklist-icon">
                          {isMatch ? <CheckCircle size={18} /> : <XCircle size={18} />}
                        </div>
                        <div className="checklist-label">Account Holder</div>
                        <div className="checklist-expected">{expected}</div>
                        <div className="checklist-extracted">{isMissing ? 'NOT FOUND' : extracted}</div>
                      </div>
                    );
                  })()}

                  {/* Amount - CRITICAL FIELD: Missing = Red */}
                  {(() => {
                    const expected = `PKR ${(selectedTransaction.amount || 0).toLocaleString()}`;
                    const extractedData = verificationResult?.extracted || verificationResult?.extracted_data || selectedTransaction.aiExtractedData || {};
                    const rawAmount = extractedData.amount || extractedData.transaction_amount;
                    // Parse amount - handle strings like "160,000", "PKR 160,000", "160000", etc.
                    const parseAmount = (val) => {
                      if (!val) return null;
                      if (typeof val === 'number') return val;
                      // Remove currency symbols, commas, spaces and parse
                      const cleaned = String(val).replace(/[PKR,\s]/gi, '').trim();
                      const num = parseFloat(cleaned);
                      return isNaN(num) ? null : num;
                    };
                    const extractedAmount = parseAmount(rawAmount);
                    const isMissing = extractedAmount === null;
                    const extracted = !isMissing ? `PKR ${extractedAmount.toLocaleString()}` : '';
                    const isMatch = !isMissing && extractedAmount === Number(selectedTransaction.amount);
                    // CRITICAL: Missing data = mismatch (red), not pending
                    return (
                      <div className={`checklist-item ${isMatch ? 'match' : 'mismatch'}`}>
                        <div className="checklist-icon">
                          {isMatch ? <CheckCircle size={18} /> : <XCircle size={18} />}
                        </div>
                        <div className="checklist-label">Amount</div>
                        <div className="checklist-expected">{expected}</div>
                        <div className="checklist-extracted">{isMissing ? 'NOT FOUND' : extracted}</div>
                      </div>
                    );
                  })()}

                  {/* Account Number - CRITICAL FIELD: Missing = Red */}
                  {(() => {
                    const expected = selectedTransaction.bankAccountNumber || 'N/A';
                    const extractedData = verificationResult?.extracted || verificationResult?.extracted_data || selectedTransaction.aiExtractedData || {};
                    const extracted = extractedData.sender_account || extractedData.senderAccount || extractedData.account_number || '';
                    const isMissing = !extracted;
                    const isMatch = !isMissing && (expected.includes(extracted.replace(/x/gi, '')) || extracted.includes(expected.slice(-4)));
                    // CRITICAL: Missing data = mismatch (red), not pending
                    return (
                      <div className={`checklist-item ${isMatch ? 'match' : 'mismatch'}`}>
                        <div className="checklist-icon">
                          {isMatch ? <CheckCircle size={18} /> : <XCircle size={18} />}
                        </div>
                        <div className="checklist-label">Account Number</div>
                        <div className="checklist-expected font-mono">{expected}</div>
                        <div className="checklist-extracted font-mono">{isMissing ? 'NOT FOUND' : extracted}</div>
                      </div>
                    );
                  })()}

                  {/* Bank Transaction ID - Extracted from receipt - CRITICAL: Must be found */}
                  {(() => {
                    const extractedData = verificationResult?.extracted || verificationResult?.extracted_data || selectedTransaction.ocrData || {};
                    const bankTxnId = extractedData.transaction_id || extractedData.transactionId || selectedTransaction.ocrData?.transactionId || selectedTransaction.bankTransactionId || '—';
                    const hasValue = bankTxnId !== '—' && bankTxnId;

                    return (
                      <div className={`checklist-item ${hasValue ? 'match' : 'mismatch'}`}>
                        <div className="checklist-icon">
                          {hasValue ? <CheckCircle size={18} /> : <XCircle size={18} />}
                        </div>
                        <div className="checklist-label">Bank Transaction ID</div>
                        <div className="checklist-expected">Required</div>
                        <div className="checklist-extracted font-mono">{hasValue ? bankTxnId : 'NOT FOUND'}</div>
                      </div>
                    );
                  })()}

                  {/* CNIC Validity - From User Data Only (NOT from AI) */}
                  {(() => {
                    // Look up user's CNIC expiry from brokerUsers - NO AI involved
                    const userCnic = selectedTransaction.userCnic || selectedTransaction.cnic;
                    const userName = selectedTransaction.userName || selectedTransaction.bankAccountTitle;

                    // Try to find user by CNIC first, then by name
                    let user = brokerUsers?.find(u =>
                      u.cnic === userCnic ||
                      u.cnic?.replace(/-/g, '') === userCnic?.replace(/-/g, '')
                    );

                    // If not found by CNIC, try by name
                    if (!user && userName) {
                      user = brokerUsers?.find(u =>
                        u.name?.toLowerCase() === userName?.toLowerCase()
                      );
                    }

                    const cnicExpiry = selectedTransaction.cnicExpiry || selectedTransaction.userCnicExpiry || user?.cnicExpiry;
                    const displayCnic = userCnic || user?.cnic || 'N/A';

                    let isValid = false;
                    let validityText = 'Not provided';

                    if (cnicExpiry) {
                      // Parse date - handle formats like "15-03-2028" or "2028-03-15"
                      let expiryDate;
                      if (cnicExpiry.includes('-')) {
                        const parts = cnicExpiry.split('-');
                        if (parts[0].length === 4) {
                          // YYYY-MM-DD format
                          expiryDate = new Date(cnicExpiry);
                        } else {
                          // DD-MM-YYYY format
                          expiryDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                        }
                      } else {
                        expiryDate = new Date(cnicExpiry);
                      }

                      const today = new Date();
                      // Check if CNIC is valid for at least 1 week from today
                      const oneWeekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

                      if (expiryDate > oneWeekFromNow) {
                        isValid = true;
                        validityText = 'Valid';
                      } else if (expiryDate > today) {
                        isValid = false; // Expiring within a week - show warning
                        validityText = 'Expiring Soon';
                      } else {
                        isValid = false;
                        validityText = 'Expired';
                      }
                    }

                    return (
                      <div className={`checklist-item ${isValid ? 'match' : cnicExpiry ? 'mismatch' : 'pending'}`}>
                        <div className="checklist-icon">
                          {isValid ? <CheckCircle size={18} /> : cnicExpiry ? <XCircle size={18} /> : <Clock size={18} />}
                        </div>
                        <div className="checklist-label">CNIC Validity</div>
                        <div className="checklist-expected">{displayCnic}</div>
                        <div className="checklist-extracted">{validityText}</div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer-new">
              {(['pending_review', 'pending_payment', 'manual_review', 'pending'].includes(selectedTransaction.status) || !selectedTransaction.status) ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { setSelectedTransaction(null); setVerificationResult(null); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    icon={XCircle}
                    onClick={() => handleRejectClick(selectedTransaction)}
                    loading={isProcessing}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    icon={CheckCircle}
                    onClick={() => handleApproveClick(selectedTransaction)}
                    loading={isProcessing}
                  >
                    Approve
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={() => { setSelectedTransaction(null); setVerificationResult(null); }}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className="zoom-overlay" onClick={() => setZoomedImage(null)}>
          <button className="zoom-close" onClick={() => setZoomedImage(null)}>
            <X size={28} />
          </button>
          <img
            src={zoomedImage}
            alt="Receipt Zoomed"
            className="zoomed-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* WhatsApp Message Modal */}
      {showMessageModal && messageTransaction && (
        <div className="modal-overlay" onClick={closeMessageModal}>
          <div className="modal message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <MessageSquare size={20} />
                Send WhatsApp Message
              </h2>
              <button className="modal-close" onClick={closeMessageModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {!messageSent ? (
                <>
                  <div className="recipient-info">
                    <div className="recipient-avatar">
                      {messageTransaction.userName?.charAt(0)}
                    </div>
                    <div className="recipient-details">
                      <span className="recipient-name">{messageTransaction.userName}</span>
                      <span className="recipient-phone">
                        <Phone size={14} />
                        {messageTransaction.userPhone}
                      </span>
                    </div>
                  </div>

                  <div className="message-type-section">
                    <label className="section-label">Select Message Template</label>
                    <div className="message-type-options">
                      <label className={`message-type-option ${messageType === 'payment_received' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="messageType"
                          value="payment_received"
                          checked={messageType === 'payment_received'}
                          onChange={(e) => setMessageType(e.target.value)}
                        />
                        <CheckCircle size={18} />
                        <span>Payment Received</span>
                      </label>
                      <label className={`message-type-option ${messageType === 'payment_confirmed' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="messageType"
                          value="payment_confirmed"
                          checked={messageType === 'payment_confirmed'}
                          onChange={(e) => setMessageType(e.target.value)}
                        />
                        <CheckCircle size={18} />
                        <span>Payment Confirmed</span>
                      </label>
                      <label className={`message-type-option ${messageType === 'payment_issue' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="messageType"
                          value="payment_issue"
                          checked={messageType === 'payment_issue'}
                          onChange={(e) => setMessageType(e.target.value)}
                        />
                        <AlertTriangle size={18} />
                        <span>Payment Issue</span>
                      </label>
                      <label className={`message-type-option ${messageType === 'custom' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="messageType"
                          value="custom"
                          checked={messageType === 'custom'}
                          onChange={(e) => setMessageType(e.target.value)}
                        />
                        <Send size={18} />
                        <span>Custom Message</span>
                      </label>
                    </div>
                  </div>

                  <div className="message-content-section">
                    <label className="section-label">
                      {messageType === 'custom' ? 'Enter Your Message' : 'Message Preview'}
                    </label>
                    {messageType === 'custom' ? (
                      <textarea
                        className="custom-message-input"
                        placeholder="Type your custom message here..."
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        rows={6}
                      />
                    ) : (
                      <div className="message-preview">
                        <pre>{messageTemplates[messageType](messageTransaction)}</pre>
                      </div>
                    )}
                  </div>

                  <div className="transaction-summary">
                    <div className="summary-item">
                      <span className="label">Reference</span>
                      <span className="value">{messageTransaction.referenceNumber}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Amount</span>
                      <span className="value">PKR {(messageTransaction.amount || 0).toLocaleString()}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Status</span>
                      <span className="value">{messageTransaction.status}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="message-sent-success">
                  <div className="success-icon">
                    <CheckCircle size={64} />
                  </div>
                  <h3>Message Sent Successfully!</h3>
                  <p>WhatsApp message has been sent to {messageTransaction.userName}</p>
                  <span className="phone-sent">{messageTransaction.userPhone}</span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {!messageSent ? (
                <>
                  <Button variant="secondary" onClick={closeMessageModal}>
                    Cancel
                  </Button>
                  <Button
                    variant="success"
                    icon={Send}
                    onClick={handleSendMessage}
                    loading={isSendingMessage}
                    disabled={messageType === 'custom' && !customMessage.trim()}
                  >
                    Send Message
                  </Button>
                </>
              ) : (
                <Button variant="primary" onClick={closeMessageModal}>
                  Done
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApproveModal && pendingTransaction && (
        <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="modal decision-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header approve-header">
              <h2>
                <CheckCircle size={20} />
                Approve Transaction
              </h2>
              <button className="modal-close" onClick={() => setShowApproveModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              <div className="decision-transaction-info">
                <div className="info-row">
                  <span className="label">Reference:</span>
                  <span className="value">{pendingTransaction.referenceNumber}</span>
                </div>
                <div className="info-row">
                  <span className="label">Customer:</span>
                  <span className="value">{pendingTransaction.userName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Amount:</span>
                  <span className="value amount">PKR {(pendingTransaction.amount || 0).toLocaleString()}</span>
                </div>
              </div>

              <div className="comment-section">
                <label className="comment-label">Admin Comment (Optional)</label>
                <textarea
                  className="comment-input"
                  placeholder="Add a comment for this approval..."
                  value={approveComment}
                  onChange={(e) => setApproveComment(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowApproveModal(false)}>
                Cancel
              </Button>
              <Button
                variant="success"
                icon={CheckCircle}
                onClick={handleApproveConfirm}
                loading={isProcessing}
              >
                Confirm Approval
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && pendingTransaction && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal decision-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header reject-header">
              <h2>
                <XCircle size={20} />
                Reject Transaction
              </h2>
              <button className="modal-close" onClick={() => setShowRejectModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              <div className="decision-transaction-info">
                <div className="info-row">
                  <span className="label">Reference:</span>
                  <span className="value">{pendingTransaction.referenceNumber}</span>
                </div>
                <div className="info-row">
                  <span className="label">Customer:</span>
                  <span className="value">{pendingTransaction.userName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Amount:</span>
                  <span className="value amount">PKR {(pendingTransaction.amount || 0).toLocaleString()}</span>
                </div>
              </div>

              <div className="comment-section">
                <label className="comment-label">Rejection Reason <span className="required">*</span></label>
                <textarea
                  className="comment-input"
                  placeholder="Please provide a reason for rejection..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                />
                {!rejectReason.trim() && (
                  <span className="error-hint">Rejection reason is required</span>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                icon={XCircle}
                onClick={handleRejectConfirm}
                loading={isProcessing}
                disabled={!rejectReason.trim()}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionReview;
